import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import ProjectDetail from './ProjectDetail';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './ConfirmModal';
import ProjectFormDrawer from './ProjectFormDrawer';
import ProjectsListView from './ProjectsListView';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useProjects,
  useUsers,
  useCreateProject,
  useUpdateProject,
  useProjectStats,
  useDeletedProjects,
  useDeleteProject,
  useRestoreProject,
} from '../hooks/api';

const ProjectsPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: projectsData,
    isLoading: projectsLoading,
    refetch: fetchProjects,
  } = useProjects();

  const {
    data: usersData,
    isLoading: usersLoading,
  } = useUsers();

  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const deleteProjectMutation = useDeleteProject();
  const restoreProjectMutation = useRestoreProject();

  const { data: projectStatsData } = useProjectStats();
  const { data: deletedProjectsData, isLoading: deletedProjectsLoading } = useDeletedProjects({ enabled: user?.role === 'admin' });

  const projects = useMemo(() => projectsData?.results || [], [projectsData]);
  const teamMembers = useMemo(
    () => (Array.isArray(usersData) ? usersData : (usersData?.results || [])),
    [usersData]
  );
  const deletedProjectsCount = projectStatsData?.deleted_projects || 0;
  const deletedProjects = deletedProjectsData?.results || [];

  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    client: '',
    is_billable: '',
    member_id: '',
    hourly_rate_min: '',
    hourly_rate_max: '',
    created_after: '',
    created_before: '',
    has_documents: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  // Project leads see the full list by default; "mine" = created by them or they're a member.
  const [projectScope, setProjectScope] = useState('all'); // 'all' | 'mine'
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 20, 50];
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectIdToDelete, setProjectIdToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeletedProjects, setShowDeletedProjects] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [projectIdToRestore, setProjectIdToRestore] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const handleProjectSubmit = async (projectData) => {
    if (editingProject) {
      await updateProjectMutation.mutateAsync({ id: editingProject.id, data: projectData });
      toast.success('Project updated successfully');
    } else {
      await createProjectMutation.mutateAsync(projectData);
      toast.success('Project created successfully');
    }
    await fetchProjects();
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, projectScope]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('action') === 'create') {
      setEditingProject(null);
      setShowModal(true);
    }
  }, [location.search]);

  const handleDelete = (projectId) => {
    setProjectIdToDelete(projectId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteProjectMutation.mutateAsync(projectIdToDelete);
      toast.success('Project deleted successfully');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete project');
    } finally {
      setShowDeleteModal(false);
      setProjectIdToDelete(null);
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProjectIdToDelete(null);
  };

  const handleRestore = (projectId) => {
    setProjectIdToRestore(projectId);
    setShowRestoreModal(true);
  };

  const confirmRestore = async () => {
    setIsRestoring(true);
    try {
      await restoreProjectMutation.mutateAsync(projectIdToRestore);
      toast.success('Project restored successfully');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore project');
    } finally {
      setShowRestoreModal(false);
      setProjectIdToRestore(null);
      setIsRestoring(false);
    }
  };

  const cancelRestore = () => {
    setShowRestoreModal(false);
    setProjectIdToRestore(null);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handleViewProject = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  const handleOpenProjectInNewWindow = (projectId) => {
    window.open(`${window.location.origin}/projects/${projectId}`, '_blank', 'noopener,noreferrer');
  };

  const handleBackToList = () => setSelectedProjectId(null);

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      client: '',
      is_billable: '',
      member_id: '',
      hourly_rate_min: '',
      hourly_rate_max: '',
      created_after: '',
      created_before: '',
      has_documents: '',
    });
  }, []);

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];

    return projects.filter((project) => {
      if (user?.role === 'project_lead' && projectScope === 'mine') {
        const memberIds = Array.isArray(project.member_ids) ? project.member_ids : [];
        const isMine =
          Boolean(user?.id) &&
          (project.created_by === user.id || memberIds.includes(user.id));
        if (!isMine) return false;
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          (project.name && project.name.toLowerCase().includes(searchLower)) ||
          (project.description && project.description.toLowerCase().includes(searchLower)) ||
          (project.client && project.client.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      if (filters.client) {
        if ((project.client || '').trim().toLowerCase() !== filters.client.trim().toLowerCase()) {
          return false;
        }
      }

      if (filters.is_billable !== '') {
        const isBillable = filters.is_billable === 'true';
        if (project.is_billable !== isBillable) return false;
      }

      if (filters.member_id && project.member_ids) {
        if (!project.member_ids.includes(filters.member_id)) return false;
      }

      if (filters.hourly_rate_min && project.hourly_rate) {
        if (Number(project.hourly_rate) < Number(filters.hourly_rate_min)) return false;
      }
      if (filters.hourly_rate_max && project.hourly_rate) {
        if (Number(project.hourly_rate) > Number(filters.hourly_rate_max)) return false;
      }

      if (filters.created_after && project.created_at) {
        if (new Date(project.created_at) < new Date(filters.created_after)) return false;
      }
      if (filters.created_before && project.created_at) {
        if (new Date(project.created_at) > new Date(filters.created_before)) return false;
      }

      if (filters.has_documents !== '') {
        const hasDocuments = filters.has_documents === 'true';
        const projectHasDocuments = (project.documents_count || 0) > 0;
        if (projectHasDocuments !== hasDocuments) return false;
      }

      return true;
    });
  }, [projects, searchTerm, filters, projectScope, user?.role, user?.id]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize));
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const canManageProject = (project) =>
    user?.role === 'admin' || (user?.role === 'project_lead' && project.created_by === user?.id);

  if (projectsLoading || usersLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <>
      {selectedProjectId ? (
        <ProjectDetail projectId={selectedProjectId} onBack={handleBackToList} />
      ) : (
        <ProjectsListView
          user={user}
          title={
            user?.role === 'project_lead' && projectScope === 'mine' ? 'My Projects' : 'Projects'
          }
          subtitle={
            user?.role === 'project_lead' && projectScope === 'mine'
              ? 'Projects you created or belong to'
              : 'Showing all projects'
          }
          projectScope={projectScope}
          onProjectScopeChange={setProjectScope}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters((v) => !v)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          teamMembers={teamMembers}
          projects={projects}
          filteredProjects={filteredProjects}
          paginatedProjects={paginatedProjects}
          currentPage={currentPage}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          onCreate={() => {
            setEditingProject(null);
            setShowModal(true);
          }}
          onView={handleViewProject}
          onOpenInNewWindow={handleOpenProjectInNewWindow}
          onEdit={handleEdit}
          onDelete={handleDelete}
          showDeletedProjects={showDeletedProjects}
          onToggleDeleted={() => setShowDeletedProjects((v) => !v)}
          deletedProjectsCount={deletedProjectsCount}
          deletedProjectsLoading={deletedProjectsLoading}
          deletedProjects={deletedProjects}
          onRestore={handleRestore}
          canManageProject={canManageProject}
        />
      )}

      <ProjectFormDrawer
        open={showModal}
        onClose={handleCloseModal}
        editingProject={editingProject}
        teamMembers={teamMembers}
        currentUser={user}
        onSubmit={handleProjectSubmit}
      />

      <ConfirmModal
        open={showDeleteModal}
        title="Delete Project"
        message="Are you sure you want to delete this project?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />

      <ConfirmModal
        open={showRestoreModal}
        title="Restore Project"
        message="Are you sure you want to restore this project? This will also restore all associated tasks."
        onConfirm={confirmRestore}
        onCancel={cancelRestore}
        confirmText="Restore"
        cancelText="Cancel"
        isLoading={isRestoring}
      />
    </>
  );
};

export default ProjectsPage;
